import DocLayout from '../../components/docs/DocLayout'
import { tokens } from '@/content/system'
import { radiusStyles, usePrefs } from '@/state/prefs'
import Cased from '@/components/ui/Cased'

const titleCase = (value: string) => value[0].toUpperCase() + value.slice(1)

/** The three surface layers, from the page up. */
const surfaces = [
  { role: '--mds-bg', name: 'Background', use: 'The page itself.' },
  { role: '--mds-surface', name: 'Surface', use: 'Quiet sections, cards at rest and the hero canvas.' },
  { role: '--mds-raised', name: 'Raised', use: 'What sits above the page: menus, dialogs and inputs.' },
]

export default function ShapePage() {
  const { prefs, set } = usePrefs()
  const radius = tokens.filter((token) => token.css.startsWith('--mds-radius-'))
  const shadows = tokens.filter((token) => token.group === 'elevation')

  return (
    <DocLayout section={{ label: 'Foundations', to: '/foundations' }}
      eyebrow="Foundations"
      title="Shape"
      intro={
        <p>
          How surfaces look: their corners, edges, layers and shadows, and the focus ring every control shares. Corner
          radius is themeable, so a mission can be round or square without touching a component.
        </p>
      }
      wide
    >
      <section className="doc-section" aria-labelledby="space-radius">
        <h2 id="space-radius"><Cased>Corner radius</Cased></h2>
        <p>
          Four radius tokens describe four jobs: controls, fields, cards and the hero canvas. The shape setting rewrites
          all four together. Round is the default: pill buttons and generous cards.
        </p>
        <div className="mds-segmented" role="group" aria-label="Corner radius">
          {radiusStyles.map((value) => (
            <button
              key={value}
              type="button"
              className="mds-segmented__option"
              aria-pressed={prefs.radius === value}
              onClick={() => set('radius', value)}
            >
              {titleCase(value)}
            </button>
          ))}
        </div>
        <div className="fnd-radius">
          {radius.map((token) => (
            <div key={token.css} className="fnd-radius__item">
              {/* Controls come square, such as icon buttons, and long, such as buttons. */}
              {token.css === '--mds-radius-control' ? (
                <span className="fnd-radius__pair" aria-hidden="true">
                  <span className="fnd-radius__shape fnd-radius__shape--square" style={{ borderRadius: `var(${token.css})` }} />
                  <span className="fnd-radius__shape fnd-radius__shape--bar" style={{ borderRadius: `var(${token.css})` }} />
                </span>
              ) : (
                <span className="fnd-radius__shape" style={{ borderRadius: `var(${token.css})` }} aria-hidden="true" />
              )}
              <code>{token.css}</code>
              <span className="mds-muted">{token.role}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="doc-section" aria-labelledby="space-elevation">
        <h2 id="space-elevation"><Cased>Elevation</Cased></h2>
        <p>Shadows are soft and sparing. Most surfaces separate by color alone; shadows mark what floats.</p>
        <div className="fnd-elevation">
          {shadows.map((token) => (
            <div key={token.css} className="fnd-elevation__item" style={{ boxShadow: `var(${token.css})` }}>
              <code>{token.css}</code>
              <span className="mds-muted">{token.role}</span>
            </div>
          ))}
        </div>
      </section>
      <section className="doc-section" aria-labelledby="shape-borders">
        <h2 id="shape-borders"><Cased>Borders and dividers</Cased></h2>
        <p>
          Two border roles do two jobs. <code>--mds-border</code> is a quiet hairline for dividers and resting cards.{' '}
          <code>--mds-border-strong</code> outlines anything people operate, such as fields and secondary buttons, and
          meets 3:1 on every surface. Borders are 1px; a section rule separates the parts of a page.
        </p>
        <div className="fnd-borders" aria-hidden="true">
          <div className="fnd-borders__item">
            <span className="fnd-borders__sample" />
            <code>--mds-border</code>
          </div>
          <div className="fnd-borders__item">
            <span className="fnd-borders__sample fnd-borders__sample--strong" />
            <code>--mds-border-strong</code>
          </div>
        </div>
      </section>

      <section className="doc-section" aria-labelledby="shape-focus">
        <h2 id="shape-focus"><Cased>Focus ring</Cased></h2>
        <p>
          Every control shows the same ring when it has keyboard focus: a 2px outline in <code>--mds-focus</code>, 3px
          away from the control, so it reads as separate from the control's own border. Where a scrolling container
          would clip it, the ring is drawn inside instead. In Windows high-contrast mode it takes the system highlight
          color.
        </p>
        <div className="mds-cluster fnd-focus" aria-hidden="true">
          <span className="mds-button mds-button--primary fnd-focus__sample">Button</span>
          <span className="mds-input fnd-focus__sample fnd-focus__field">Field</span>
          <span className="mds-link fnd-focus__sample">Link</span>
        </div>
      </section>

      <section className="doc-section" aria-labelledby="shape-surfaces">
        <h2 id="shape-surfaces"><Cased>Surfaces</Cased></h2>
        <p>
          Three layers stack from the page up. Most separation comes from these colors alone; a shadow marks only what
          floats over the page.
        </p>
        <div className="fnd-surfaces">
          {surfaces.map((surface) => (
            <div key={surface.role} className="fnd-surfaces__item" style={{ background: `var(${surface.role})` }}>
              <span className="fnd-surfaces__name">{surface.name}</span>
              <code>{surface.role}</code>
              <span className="mds-muted">{surface.use}</span>
            </div>
          ))}
        </div>
      </section>
    </DocLayout>
  )
}
